import type React from "react";

export type IncompatibleFile = {file: File; reason: string; index: number};

// Top padding stays inside the field so scrolled text runs to the composer's
// edge. Bottom padding would show a sliver of the next row in a capped field,
// so the wrapper carries it (pb-0 beats the forms plugin's default).
export const VARIANT_CLASSES = {
   common:
      "pb-0 w-full bg-background text-1000 resize-none focus:outline-none focus:ring-0 border-0 placeholder:text-500 dark:placeholder:text-600",
   large: "px-5 pt-5 text-base",
   compact: "px-4",
};

export const CONTROLS_CLASSES = {
   common: "bg-background",
   large: "px-4 py-2",
   compact: "px-4 py-2",
};

export const UPLOAD_BUTTON_CLASSES =
   "h-8 w-8 p-0 rounded-full bg-200 text-800 hover:bg-300 disabled:bg-100 disabled:opacity-50 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-300 transition-colors duration-200";

export function getScrollMaskStyle(canScrollLeft: boolean, canScrollRight: boolean): React.CSSProperties {
   const left = canScrollLeft ? "transparent 0px, black 20px" : "black 0px";
   const right = canScrollRight ? "calc(100% - 20px), transparent 100%" : "100%";
   const gradient = `linear-gradient(to right, ${left}, black ${right})`;
   return {maskImage: gradient, WebkitMaskImage: gradient} as React.CSSProperties;
}

const FALLBACK_LINE_HEIGHT = 24;

function readLineHeight(textarea: HTMLTextAreaElement): number {
   const measured = Number.parseFloat(getComputedStyle(textarea).lineHeight);
   return Number.isFinite(measured) && measured > 0 ? measured : FALLBACK_LINE_HEIGHT;
}

export function adjustTextareaHeight(textarea: HTMLTextAreaElement, maxRows: number) {
   const {selectionStart, selectionEnd, selectionDirection, scrollTop, scrollLeft} = textarea;
   const computed = getComputedStyle(textarea);
   const px = (value: string) => Number.parseFloat(value) || 0;
   const padding = px(computed.paddingTop) + px(computed.paddingBottom);
   const border = px(computed.borderTopWidth) + px(computed.borderBottomWidth);
   const borderBox = computed.boxSizing === "border-box";
   const lineHeight = readLineHeight(textarea);

   if (textarea.dataset.composerExpanded !== "true") {
      // Reset before measuring so the field can shrink when text is deleted.
      textarea.style.height = "auto";
      textarea.style.overflowY = "hidden";
      const contentHeight = Math.max(lineHeight, textarea.scrollHeight - padding);
      const extra = borderBox ? padding + border : 0;
      let height = Math.min(contentHeight, lineHeight * maxRows) + extra;
      textarea.style.height = `${height}px`;
      // Snap to whole rows when the viewport-dependent CSS cap limits the height.
      const cappedHeight = px(getComputedStyle(textarea).height);
      if (cappedHeight > 0 && cappedHeight < height - 0.5) {
         height = Math.max(1, Math.floor((cappedHeight - extra) / lineHeight)) * lineHeight + extra;
         textarea.style.height = `${height}px`;
      }
      textarea.style.overflowY = contentHeight + extra > height + 0.5 ? "auto" : "hidden";
   } else {
      textarea.style.overflowY = textarea.scrollHeight > textarea.clientHeight ? "auto" : "hidden";
   }
   textarea.style.overscrollBehaviorY = textarea.style.overflowY === "auto" ? "contain" : "auto";
   // Preserve the editing position across measurement and resizing.
   if (textarea.selectionStart !== selectionStart || textarea.selectionEnd !== selectionEnd || textarea.selectionDirection !== selectionDirection) {
      textarea.setSelectionRange(selectionStart, selectionEnd, selectionDirection);
   }
   textarea.scrollTop = scrollTop;
   textarea.scrollLeft = scrollLeft;
}

/**
 * Visual rows of text, including soft wraps. A textarea that has not been laid
 * out (or jsdom) reports no width, so explicit newlines are the fallback.
 * The live field may be height-locked while expanded, so a hidden clone is
 * measured instead of the field itself.
 */
export function countTextareaRows(textarea: HTMLTextAreaElement): number {
   const newlineRows = Math.max(1, textarea.value.split("\n").length);
   const width = textarea.clientWidth;
   if (width <= 0 || typeof document === "undefined") return newlineRows;

   const probe = textarea.cloneNode(false) as HTMLTextAreaElement;
   probe.removeAttribute("id");
   probe.removeAttribute("data-testid");
   probe.removeAttribute("aria-controls");
   probe.removeAttribute("aria-activedescendant");
   probe.removeAttribute("aria-expanded");
   probe.setAttribute("aria-hidden", "true");
   probe.tabIndex = -1;
   probe.rows = 1;
   probe.style.setProperty("position", "absolute");
   probe.style.setProperty("top", "0");
   probe.style.setProperty("left", "0");
   probe.style.setProperty("visibility", "hidden");
   probe.style.setProperty("pointer-events", "none");
   probe.style.setProperty("height", "0px", "important");
   probe.style.setProperty("min-height", "0", "important");
   probe.style.setProperty("max-height", "none", "important");
   probe.style.setProperty("width", `${width}px`, "important");
   const computed = getComputedStyle(textarea);
   probe.style.font = computed.font;
   probe.style.letterSpacing = computed.letterSpacing;

   const measure = (value: string) => {
      probe.value = value;
      return probe.scrollHeight;
   };

   document.body.appendChild(probe);
   try {
      const oneLine = measure("X");
      const lineHeight = measure("X\nX") - oneLine;
      if (lineHeight <= 0) return newlineRows;
      const content = measure(textarea.value);
      const wrappedRows = 1 + Math.round((content - oneLine) / lineHeight);
      return Math.max(1, newlineRows, wrappedRows);
   } finally {
      probe.remove();
   }
}
